import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { fetchData, postData } from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import { Guard } from '../Common/Guard';
import { PERMS } from '../../Utils/Permissions';
import { ReasonCaptureModal } from '../Common/ReasonCaptureModal';
import PageLayout from '../Layout/PageLayout';
import { Card, CardTitleBar, CardBody, FormField, TextInput, Button, StatusBadge, EmptyState, Spinner } from '../UI';
import { tokens } from '../../theme/tokens';

const Timeline = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Step = styled.li`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${tokens.spacing[2]};
  padding: ${tokens.spacing[4]} 0;
  border-bottom: 1px solid ${tokens.colors.divider};
  font-size: ${tokens.typography.size.base};
  color: ${tokens.colors.body};
  &:last-child { border-bottom: 0; }

  code {
    font-family: ${tokens.typography.fonts.mono};
    font-size: ${tokens.typography.size.sm};
    background: ${tokens.colors.surfaceSunken};
    padding: 2px 6px;
    border-radius: ${tokens.radii.sm};
  }
`;

const Role = styled.strong`
  color: ${tokens.colors.heading};
`;

const Meta = styled.span`
  color: ${tokens.colors.muted};
`;

export default function ChainOfCustody() {
  const { id } = useParams();
  const [chain, setChain] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newRiderId, setNewRiderId] = useState('');
  const [pendingReason, setPendingReason] = useState(false);

  const loadChain = () =>
    fetchData(Endpoints.CHAIN_OF_CUSTODY(id))
      .then((res) => res.json())
      .then((res) => {
        const list = (res && res.data) || res || [];
        setChain(Array.isArray(list) ? list : []);
      });

  useEffect(() => {
    loadChain().finally(() => setLoading(false));
  }, [id]);

  const submitManualHandover = (reason) => {
    if (!newRiderId.trim()) return;
    const fd = new FormData();
    fd.append('request_id', id);
    fd.append('new_rider_id', newRiderId.trim());
    fd.append('reason', reason);
    postData(Endpoints.MANUAL_HANDOVER, fd)
      .then((res) => res.json())
      .then(() => {
        setPendingReason(false);
        setNewRiderId('');
        loadChain();
      });
  };

  if (loading) return <PageLayout title={`Chain of custody — request ${id}`}><Spinner center label="Loading…" /></PageLayout>;

  return (
    <PageLayout title={`Chain of custody — request ${id}`}>
      <Card padded={false} style={{ marginBottom: tokens.spacing[5] }}>
        <CardTitleBar title="Chain of custody" />
        {chain.length === 0 ? (
          <EmptyState title="No involved riders recorded" description="No handover history exists for this request yet." />
        ) : (
          <CardBody>
            <Timeline>
              {chain.map((r) => (
                <Step key={r.id || `${r.rider_id}-${r.role}`}>
                  <Role>{r.role || 'R?'}</Role>
                  <Meta>&mdash; rider <code>{r.rider_id}</code></Meta>
                  {r.status && <StatusBadge status={r.status} size="sm" />}
                  {r.assigned_date_time && <Meta>&middot; assigned: {r.assigned_date_time}</Meta>}
                  {r.signed_off_date_time && <Meta>&middot; signed off: {r.signed_off_date_time}</Meta>}
                </Step>
              ))}
            </Timeline>
          </CardBody>
        )}
      </Card>

      {/* Manual handover — only visible to admins with REQUEST_ASSIGN permission */}
      <Guard permission={PERMS.REQUEST_ASSIGN}>
        <Card tone="muted">
          <h3
            style={{
              margin: '0 0 6px',
              fontSize: tokens.typography.size.lg,
              fontWeight: tokens.typography.weight.bold,
              color: tokens.colors.heading,
            }}
          >
            Manual handover
          </h3>
          <p style={{ margin: '0 0 16px', color: tokens.colors.muted, lineHeight: 1.5 }}>
            Force-assign this request to a different rider. Use sparingly &mdash; this bypasses
            the normal handover flow.
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: tokens.spacing[3], flexWrap: 'wrap' }}>
            <FormField label="New rider ID" htmlFor="new-rider-id" flush>
              <TextInput
                id="new-rider-id"
                type="text"
                placeholder="New rider ID"
                value={newRiderId}
                onChange={(e) => setNewRiderId(e.target.value)}
                style={{ maxWidth: 260 }}
              />
            </FormField>
            <Button
              disabled={!newRiderId.trim()}
              onClick={() => setPendingReason(true)}
            >
              Reassign rider
            </Button>
          </div>
          {pendingReason && (
            <ReasonCaptureModal
              title="Manually reassign rider"
              onCancel={() => setPendingReason(false)}
              onConfirm={submitManualHandover}
            />
          )}
        </Card>
      </Guard>
    </PageLayout>
  );
}
